import { useEffect, useState } from "react";
import { Logger, formatError } from "../packages/logger/src";
import { whenDeviceRegistered } from "../packages/widgets/src/services/WebexInstanceService";

/**
 * Read the authenticated Webex ID only after the device registration is complete.
 *
 * @returns {string | undefined} the registered Webex user ID, when available
 */
function getRegisteredWebexUserId(): string | undefined {
  const device = window.webex?.internal?.device;
  if (!device?.registered || typeof device.userId !== "string") {
    return undefined;
  }

  const userId = device.userId.trim();
  return userId || undefined;
}

/**
 * Returns the authenticated Webex device user ID after device registration.
 * The storage listener also handles an account/device change during re-auth.
 *
 * @param {boolean} enabled whether the identity should be resolved
 * @returns {string | undefined} the registered Webex user ID, when available
 */
export function useWebexUserId(enabled = true): string | undefined {
  const [userId, setUserId] = useState<string | undefined>(() =>
    enabled ? getRegisteredWebexUserId() : undefined
  );

  useEffect(() => {
    let isMounted = true;

    const syncUserId = () => {
      if (!isMounted) {
        return;
      }
      const nextUserId = getRegisteredWebexUserId();
      setUserId((currentUserId) =>
        currentUserId === nextUserId ? currentUserId : nextUserId
      );
    };

    if (!enabled) {
      setUserId(undefined);
      return () => {
        isMounted = false;
      };
    }

    window.addEventListener("storage", syncUserId);
    syncUserId();

    whenDeviceRegistered()
      .then(syncUserId)
      .catch((error: unknown) => {
        if (isMounted) {
          Logger.info(`useWebexUserId#resolve() :: ${formatError(error)}`);
        }
      });

    return () => {
      isMounted = false;
      window.removeEventListener("storage", syncUserId);
    };
  }, [enabled]);

  return userId;
}
